import { Injectable } from '@angular/core';
import { ApiService } from '@core/api/api.service';
import {
  BillingFailureSummary,
  BillingReconciliationMismatchSummary,
  BillingRefundSummary,
  BillingReconciliationRunSummary,
  BillingDisputeListResult,
  BillingDisputeSummary,
  BillingInvoiceListResult,
  BillingInvoiceSummary,
  GpAddonStripeSubscribeResponse,
  GpTierDowngradeResponse,
  GpTierStripeSubscribeResponse,
  LpStripeSubscribeResponse,
  SepaPaymentMethodRecordResponse,
  SepaSetupIntentResponse,
  StripeFrontendConfig,
  StripeSepaBillingAddress,
  StripeSepaMandateConfirmationResult,
} from '@core/models/stripe-billing.model';
import { Observable, throwError } from 'rxjs';
import { of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import {
  loadStripe,
  Stripe,
  StripeElements,
  StripeIbanElement,
  StripeIbanElementChangeEvent,
  StripeError,
} from '@stripe/stripe-js';
import { environment } from 'src/environments/environment';

export interface BillingListQuery {
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
  status?: string;
  createdAfter?: string;
  createdBefore?: string;
}

/**
 * LP subscribe was sent without SEPA details for a subscription that charges now
 * (the org already used its trial, or the tier has none) — collect SEPA and retry.
 */
export function isPaymentMethodRequiredError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false;
  }
  const { status, error: body } = error as { status?: number; error?: unknown };
  return (
    status === 400 &&
    !!body &&
    typeof body === 'object' &&
    (body as { code?: unknown }).code === 'payment_method_required'
  );
}

@Injectable({ providedIn: 'root' })
export class StripeService {
  private readonly configEndpointCandidates = [
    'subscriptions/billing/frontend-config/',
    'subscriptions/billing/sepa/frontend-config/',
    'subscriptions/billing/config/',
  ];
  private readonly invoiceEndpointCandidates = ['subscriptions/billing/invoices/', 'common/subscription/billing/invoices/'];
  private readonly disputeEndpointCandidates = ['subscriptions/billing/disputes/', 'common/subscription/billing/disputes/'];

  private readonly stripeConfig$ = this.loadStripeFrontendConfig().pipe(shareReplay(1));
  private stripeInstance$: Observable<Stripe | null> | null = null;

  constructor(private apiService: ApiService) {}

  getStripeConfig(): Observable<StripeFrontendConfig> {
    return this.stripeConfig$;
  }

  getStripeInstance(): Observable<Stripe | null> {
    if (!this.stripeInstance$) {
      this.stripeInstance$ = this.getStripeConfig().pipe(
        switchMap((config) => loadStripe(config.publishableKey)),
        shareReplay(1),
      );
    }
    return this.stripeInstance$;
  }

  createSepaSetupIntent(): Observable<SepaSetupIntentResponse> {
    return this.apiService.post<any>('subscriptions/billing/sepa/setup-intent/', {}).pipe(
      map((response) => ({
        setupIntentId: response.setupIntentId || response.setup_intent_id,
        clientSecret: response.clientSecret || response.client_secret,
      })),
    );
  }

  recordSepaPaymentMethod(paymentMethodId: string, setupIntentId?: string): Observable<SepaPaymentMethodRecordResponse> {
    const payload: Record<string, string> = { payment_method_id: paymentMethodId };
    if (setupIntentId) {
      payload.setup_intent_id = setupIntentId;
    }
    return this.apiService.post<any>('subscriptions/billing/sepa/confirm/', payload).pipe(
      map((response) => ({
        paymentMethodId: response.paymentMethodId || response.payment_method_id,
        last4: response.last4 || null,
        mandateStatus: response.mandateStatus || response.mandate_status || null,
      })),
    );
  }

  /**
   * `paymentMethodId` may be null for a tier that starts a free trial: SEPA details
   * are then collected before the trial ends (KAP-1897). When the org turns out not
   * to be trial-eligible, the backend rejects a null with
   * `code: 'payment_method_required'` — see `isPaymentMethodRequiredError`.
   */
  subscribeLpTier(
    tierCode: string,
    billingInterval: 'annual' | 'monthly',
    paymentMethodId: string | null,
  ): Observable<LpStripeSubscribeResponse> {
    const payload: Record<string, string> = {
      tier_code: tierCode,
      billing_interval: billingInterval,
    };
    if (paymentMethodId) {
      payload.payment_method_id = paymentMethodId;
    }
    return this.apiService.post<any>('subscriptions/billing/lp/subscribe/', payload).pipe(
      map((response) => ({
        stripeSubscriptionId: response.stripeSubscriptionId || response.stripe_subscription_id,
        paymentStatus: response.paymentStatus || response.payment_status,
        trialEnd: response.trialEnd || response.trial_end || null,
        paymentMethodRequired: response.paymentMethodRequired ?? response.payment_method_required ?? false,
      })),
    );
  }

  upgradeLpTier(tierCode: string, billingInterval: 'annual' | 'monthly'): Observable<LpStripeSubscribeResponse> {
    return this.apiService
      .post<any>('subscriptions/billing/lp/upgrade/', {
        tier_code: tierCode,
        billing_interval: billingInterval,
      })
      .pipe(
        map((response) => ({
          stripeSubscriptionId: response.stripeSubscriptionId || response.stripe_subscription_id,
          paymentStatus: response.paymentStatus || response.payment_status,
          trialEnd: response.trialEnd || response.trial_end || null,
        })),
      );
  }

  subscribeGpAddon(addonCode: string, paymentMethodId: string): Observable<GpAddonStripeSubscribeResponse> {
    return this.apiService
      .post<any>('subscriptions/billing/gp/addon/subscribe/', {
        addon_code: addonCode,
        payment_method_id: paymentMethodId,
      })
      .pipe(
        map((response) => ({
          stripeSubscriptionId: response.stripeSubscriptionId || response.stripe_subscription_id,
          paymentStatus: response.paymentStatus || response.payment_status,
        })),
      );
  }

  // KAP-1849. Never call subscribeGpTier for gp_free - GPSubscribeView returns
  // a different, non-billing response shape ({detail: 'Switched to free tier.'})
  // for that tier; the free-tier switch stays on the existing DB-only
  // upgradeTier()/common/subscription/upgrade/ path.
  subscribeGpTier(tierCode: string, paymentMethodId: string): Observable<GpTierStripeSubscribeResponse> {
    return this.apiService
      .post<any>('subscriptions/billing/gp/subscribe/', {
        tier_code: tierCode,
        payment_method_id: paymentMethodId,
      })
      .pipe(
        map((response) => ({
          stripeSubscriptionId: response.stripeSubscriptionId || response.stripe_subscription_id,
          paymentStatus: response.paymentStatus || response.payment_status,
          accessState: response.accessState || response.access_state,
          pendingActivation: response.pendingActivation ?? response.pending_activation,
        })),
      );
  }

  // KAP-1849. No billing_interval - GP tiers are monthly-only. Single-call
  // immediate switch (GPUpgradeView), unlike LP's separate preview/confirm.
  upgradeGpTier(tierCode: string): Observable<GpTierStripeSubscribeResponse> {
    return this.apiService
      .post<any>('subscriptions/billing/gp/upgrade/', {
        tier_code: tierCode,
      })
      .pipe(
        map((response) => ({
          stripeSubscriptionId: response.stripeSubscriptionId || response.stripe_subscription_id,
          paymentStatus: response.paymentStatus || response.payment_status,
          accessState: response.accessState || response.access_state,
          pendingActivation: response.pendingActivation ?? response.pending_activation,
        })),
      );
  }

  downgradeGpTier(tierCode: string): Observable<GpTierDowngradeResponse> {
    return this.apiService
      .post<any>('subscriptions/billing/gp/downgrade/', {
        tier_code: tierCode,
      })
      .pipe(
        map((response) => ({
          stripeSubscriptionId: response.stripeSubscriptionId || response.stripe_subscription_id,
          cancelAtPeriodEnd: response.cancelAtPeriodEnd ?? response.cancel_at_period_end,
          currentPeriodEnd: response.currentPeriodEnd || response.current_period_end || null,
          pendingDowngradeTierCode: response.pendingDowngradeTierCode || response.pending_downgrade_tier_code || null,
        })),
      );
  }

  listBillingInvoices(query?: BillingListQuery): Observable<BillingInvoiceListResult> {
    const options = this.toHttpGetOptions(query);
    return this.requestFirstSuccessful<any>(this.invoiceEndpointCandidates, 0, options).pipe(
      map((response) => ({
        available: true,
        items: this.parseListResponse(response).map((invoice) => this.mapInvoiceSummary(invoice)),
      })),
      catchError((error) => {
        if (error?.status === 403 || error?.status === 404) {
          return of({ available: false, items: [] });
        }
        return throwError(() => error);
      }),
    );
  }

  listBillingDisputes(query?: BillingListQuery): Observable<BillingDisputeListResult> {
    const options = this.toHttpGetOptions(query);
    return this.requestFirstSuccessful<any>(this.disputeEndpointCandidates, 0, options).pipe(
      map((response) => ({
        available: true,
        items: this.parseListResponse(response).map((dispute) => this.mapDisputeSummary(dispute)),
      })),
      catchError((error) => {
        if (error?.status === 403 || error?.status === 404) {
          return of({ available: false, items: [] });
        }
        return throwError(() => error);
      }),
    );
  }

  listBillingFailures(query?: BillingListQuery): Observable<BillingFailureSummary[]> {
    return this.apiService.get<any>('subscriptions/billing/failures/', this.toHttpGetOptions(query)).pipe(
      map((response) => this.parseListResponse(response).map((failure) => this.mapFailureSummary(failure))),
    );
  }

  listBillingRefunds(query?: BillingListQuery): Observable<BillingRefundSummary[]> {
    return this.apiService.get<any>('subscriptions/billing/refunds/', this.toHttpGetOptions(query)).pipe(
      map((response) => this.parseListResponse(response).map((refund) => this.mapRefundSummary(refund))),
    );
  }

  listReconciliationRuns(query?: BillingListQuery): Observable<BillingReconciliationRunSummary[]> {
    return this.apiService.get<any>('subscriptions/billing/reconciliation/', this.toHttpGetOptions(query)).pipe(
      map((response) => this.parseListResponse(response).map((run) => this.mapReconciliationRun(run))),
    );
  }

  runReconciliation(periodStart?: string, periodEnd?: string): Observable<BillingReconciliationRunSummary> {
    const payload: Record<string, string> = {};
    if (periodStart) {
      payload.period_start = periodStart;
    }
    if (periodEnd) {
      payload.period_end = periodEnd;
    }
    return this.apiService.post<any>('subscriptions/billing/reconciliation/', payload).pipe(
      map((run) => this.mapReconciliationRun(run)),
    );
  }

  resolveReconciliationMismatch(mismatchUid: string, resolutionNotes: string = ''): Observable<any> {
    return this.apiService.patch<any>(`subscriptions/billing/reconciliation/mismatches/${mismatchUid}/resolve/`, {
      resolution_notes: resolutionNotes,
    });
  }

  issueInvoiceRefund(invoiceUid: string, amountEur?: number, reason?: string): Observable<any> {
    const payload: Record<string, unknown> = {};
    if (typeof amountEur === 'number' && !Number.isNaN(amountEur)) {
      payload.amount_eur = amountEur;
    }
    if (reason) {
      payload.reason = reason;
    }
    return this.apiService.post<any>(`subscriptions/billing/invoices/${invoiceUid}/refund/`, payload);
  }

  createElements(stripe: Stripe): StripeElements {
    return stripe.elements();
  }

  createIbanElement(elements: StripeElements): StripeIbanElement {
    return elements.create('iban', {
      supportedCountries: ['SEPA'],
    });
  }

  isIbanComplete(event: StripeIbanElementChangeEvent): boolean {
    return event.complete && !event.error;
  }

  /**
   * Whether Stripe will require a full billing address for this debtor country.
   *
   * SEPA is wider than the EEA — it also covers the UK, Switzerland, Monaco,
   * San Marino, Andorra and the Vatican. For accounts outside the EEA, Stripe
   * rejects `confirmSepaDebitSetup` with
   * `Missing required param: payment_method_data[billing_details][address][line1]`.
   * EEA debtors need no address, so we only ask when we have to.
   */
  requiresBillingAddress(ibanCountry: string | null | undefined): boolean {
    if (!ibanCountry) {
      return false;
    }
    return !StripeService.EEA_COUNTRIES.has(ibanCountry.trim().toUpperCase());
  }

  /** EU 27 plus Iceland, Liechtenstein and Norway. */
  private static readonly EEA_COUNTRIES = new Set<string>([
    'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR',
    'HR', 'HU', 'IE', 'IS', 'IT', 'LI', 'LT', 'LU', 'LV', 'MT', 'NL', 'NO',
    'PL', 'PT', 'RO', 'SE', 'SI', 'SK',
  ]);

  async confirmSepaMandate(
    stripe: Stripe,
    clientSecret: string,
    ibanElement: StripeIbanElement,
    billingName: string,
    billingEmail?: string,
    billingAddress?: StripeSepaBillingAddress | null,
  ): Promise<StripeSepaMandateConfirmationResult> {
    // A SetupIntent can only be confirmed once. If a previous attempt already
    // confirmed it (e.g. the mandate succeeded but a later step like the add-on
    // subscribe failed and the user retried), confirming again throws
    // `setup_intent_unexpected_state`. Reuse the existing payment method instead.
    const existing = await stripe.retrieveSetupIntent(clientSecret);
    if (!existing.error && existing.setupIntent?.status === 'succeeded') {
      const existingPaymentMethodId =
        typeof existing.setupIntent.payment_method === 'string' ? existing.setupIntent.payment_method : '';
      if (existingPaymentMethodId) {
        return {
          setupIntentId: existing.setupIntent.id,
          paymentMethodId: existingPaymentMethodId,
        };
      }
    }

    const confirmation = await stripe.confirmSepaDebitSetup(clientSecret, {
      payment_method: {
        sepa_debit: ibanElement,
        billing_details: {
          name: billingName,
          email: billingEmail || undefined,
          // Omit the key entirely when we have no address. Sending an object of
          // empty strings is worse than sending nothing: Stripe treats "" as a
          // supplied-but-invalid value.
          ...(billingAddress ? { address: this.toStripeAddress(billingAddress) } : {}),
        },
      },
    });

    if (confirmation.error) {
      throw confirmation.error;
    }

    const setupIntent = confirmation.setupIntent;
    const paymentMethodId = typeof setupIntent.payment_method === 'string' ? setupIntent.payment_method : '';
    if (!paymentMethodId) {
      throw new Error('No payment method returned from Stripe setup confirmation.');
    }

    return {
      setupIntentId: setupIntent.id,
      paymentMethodId,
    };
  }

  /** Drop empty optional fields so Stripe sees absent rather than blank. */
  private toStripeAddress(address: StripeSepaBillingAddress): Record<string, string> {
    const mapped: Record<string, string> = {
      line1: address.line1,
      city: address.city,
      postal_code: address.postalCode,
      country: address.country,
    };
    if (address.line2) {
      mapped['line2'] = address.line2;
    }
    return mapped;
  }

  normalizeStripeError(error: unknown): string {
    const stripeError = error as StripeError | null;
    return stripeError?.message || 'Stripe payment setup failed. Please try again.';
  }

  private loadStripeFrontendConfig(): Observable<StripeFrontendConfig> {
    return this.tryFetchConfigByIndex(0).pipe(
      catchError(() => {
        const fallbackKey = environment.stripePublishableKey;
        if (!fallbackKey) {
          return throwError(
            () =>
              new Error(
                'Stripe publishable key is not configured. Add backend frontend-config endpoint or set stripePublishableKey in environment.',
              ),
          );
        }
        return this.toStripeConfig({ publishableKey: fallbackKey });
      }),
    );
  }

  private tryFetchConfigByIndex(index: number): Observable<StripeFrontendConfig> {
    if (index >= this.configEndpointCandidates.length) {
      return throwError(() => new Error('Stripe frontend config endpoint not available.'));
    }

    const endpoint = this.configEndpointCandidates[index];
    return this.apiService.get<any>(endpoint).pipe(
      switchMap((response) => this.toStripeConfig(response)),
      catchError(() => this.tryFetchConfigByIndex(index + 1)),
    );
  }

  private requestFirstSuccessful<T>(endpoints: string[], index: number, options?: { params: Record<string, string> }): Observable<T> {
    if (index >= endpoints.length) {
      return throwError(() => new Error('No endpoint candidate available.'));
    }
    return this.apiService.get<T>(endpoints[index], options).pipe(
      catchError((error) => {
        if (index === endpoints.length - 1) {
          return throwError(() => error);
        }
        return this.requestFirstSuccessful<T>(endpoints, index + 1, options);
      }),
    );
  }

  private toHttpGetOptions(query?: BillingListQuery): { params: Record<string, string> } | undefined {
    if (!query) {
      return undefined;
    }
    const params: Record<string, string> = {};
    if (query.page != null) {
      params.page = String(query.page);
    }
    if (query.pageSize != null) {
      params.page_size = String(query.pageSize);
    }
    if (query.ordering) {
      params.ordering = query.ordering;
    }
    if (query.search) {
      params.search = query.search;
    }
    if (query.status) {
      params.status = query.status;
    }
    if (query.createdAfter) {
      params.created_after = query.createdAfter;
    }
    if (query.createdBefore) {
      params.created_before = query.createdBefore;
    }
    return Object.keys(params).length ? { params } : undefined;
  }

  private parseListResponse(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }
    if (Array.isArray(response?.results)) {
      return response.results;
    }
    return [];
  }

  private mapCustomerFields(raw: any): Pick<BillingInvoiceSummary, 'customerName' | 'customerEmail' | 'organizationName'> {
    return {
      customerName: raw.customerName || raw.customer_name || '',
      customerEmail: raw.customerEmail || raw.customer_email || '',
      organizationName: raw.organizationName || raw.organization_name || '',
    };
  }

  private mapInvoiceSummary(raw: any): BillingInvoiceSummary {
    return {
      ...this.mapCustomerFields(raw),
      uid: raw.uid,
      stripeInvoiceId: raw.stripeInvoiceId || raw.stripe_invoice_id || '',
      invoiceStatus: raw.invoiceStatus || raw.invoice_status || '',
      paymentStatus: raw.paymentStatus || raw.payment_status || '',
      amountDue: Number(raw.amountDue ?? raw.amount_due ?? 0),
      amountPaid: Number(raw.amountPaid ?? raw.amount_paid ?? 0),
      currency: raw.currency || 'eur',
      hostedInvoiceUrl: raw.hostedInvoiceUrl || raw.hosted_invoice_url || null,
      invoicePdfUrl: raw.invoicePdfUrl || raw.invoice_pdf_url || null,
      periodStart: raw.periodStart || raw.period_start || null,
      periodEnd: raw.periodEnd || raw.period_end || null,
      paidAt: raw.paidAt || raw.paid_at || null,
      failureMessage: raw.failureMessage || raw.failure_message || null,
      refundedAmount: Number(raw.refundedAmount ?? raw.refunded_amount ?? 0),
      refundableAmount: Number(raw.refundableAmount ?? raw.refundable_amount ?? 0),
      createdAt: raw.createdAt || raw.created_at || null,
    };
  }

  private mapDisputeSummary(raw: any): BillingDisputeSummary {
    const stripeInvoice = raw.stripeInvoice ?? raw.stripe_invoice;
    return {
      ...this.mapCustomerFields(raw),
      uid: raw.uid,
      disputeId: raw.stripeDisputeId || raw.stripe_dispute_id || '',
      stripeInvoiceUid:
        raw.stripeInvoiceUid ||
        raw.stripe_invoice_uid ||
        (typeof stripeInvoice === 'string' ? stripeInvoice : stripeInvoice?.uid) ||
        null,
      amount: Number(raw.amount ?? 0),
      currency: raw.currency || 'eur',
      reason: raw.reason || '',
      status: raw.status || '',
      isResolved: Boolean(raw.isResolved ?? raw.is_resolved ?? false),
      evidenceDeadline: raw.evidenceDeadline || raw.evidence_deadline || null,
      stripeEvidenceUrl: raw.stripeEvidenceUrl || raw.stripe_evidence_url || null,
      createdAt: raw.createdAt || raw.created_at || null,
    };
  }

  private mapFailureSummary(raw: any): BillingFailureSummary {
    const stripeInvoice = raw.stripeInvoice ?? raw.stripe_invoice;
    const billingSub = raw.stripeBillingSubscription ?? raw.stripe_billing_subscription;
    return {
      ...this.mapCustomerFields(raw),
      uid: raw.uid,
      stripeInvoiceUid:
        raw.stripeInvoiceUid ||
        raw.stripe_invoice_uid ||
        (typeof stripeInvoice === 'string' ? stripeInvoice : stripeInvoice?.uid) ||
        null,
      stripeBillingSubscriptionUid:
        raw.stripeBillingSubscriptionUid ||
        raw.stripe_billing_subscription_uid ||
        (typeof billingSub === 'string' ? billingSub : billingSub?.uid) ||
        null,
      amount: Number(raw.amount ?? 0),
      currency: raw.currency || 'eur',
      failureCode: raw.failureCode || raw.failure_code || '',
      failureMessage: raw.failureMessage || raw.failure_message || '',
      status: raw.status || '',
      retryCount: Number(raw.retryCount ?? raw.retry_count ?? 0),
      maxRetries: Number(raw.maxRetries ?? raw.max_retries ?? 0),
      nextRetryAt: raw.nextRetryAt || raw.next_retry_at || null,
      resolvedAt: raw.resolvedAt || raw.resolved_at || null,
      resolutionNotes: raw.resolutionNotes || raw.resolution_notes || null,
      createdAt: raw.createdAt || raw.created_at || null,
    };
  }

  private mapRefundSummary(raw: any): BillingRefundSummary {
    return {
      ...this.mapCustomerFields(raw),
      uid: raw.uid,
      refundId: raw.stripeRefundId || raw.stripe_refund_id || '',
      amount: Number(raw.amount ?? 0),
      currency: raw.currency || 'eur',
      status: raw.status || '',
      reason: raw.reason || '',
      createdAt: raw.createdAt || raw.created_at || null,
    };
  }

  private mapReconciliationRun(raw: any): BillingReconciliationRunSummary {
    return {
      uid: raw.uid,
      startedAt: raw.startedAt || raw.started_at || null,
      completedAt: raw.completedAt || raw.completed_at || null,
      status: raw.status || '',
      mismatchCount: Number(raw.mismatchCount ?? raw.mismatch_count ?? 0),
      totalInvoiced: Number(raw.totalInvoiced ?? raw.total_invoiced ?? 0),
      totalCollected: Number(raw.totalCollected ?? raw.total_collected ?? 0),
      totalOutstanding: Number(raw.totalOutstanding ?? raw.total_outstanding ?? 0),
      errorMessage: raw.errorMessage || raw.error_message || null,
      mismatches: Array.isArray(raw.mismatches)
        ? raw.mismatches.map((mismatch: any) => this.mapReconciliationMismatch(mismatch))
        : [],
    };
  }

  private mapReconciliationMismatch(raw: any): BillingReconciliationMismatchSummary {
    return {
      uid: raw.uid,
      mismatchType: raw.mismatchType || raw.mismatch_type || '',
      description: raw.description || '',
      isResolved: Boolean(raw.isResolved ?? raw.is_resolved ?? false),
      stripeInvoiceId: raw.stripeInvoiceId || raw.stripe_invoice_id || '',
      stripeSubscriptionId: raw.stripeSubscriptionId || raw.stripe_subscription_id || '',
      internalAmount: raw.internalAmount != null ? Number(raw.internalAmount) : raw.internal_amount != null ? Number(raw.internal_amount) : null,
      stripeAmount: raw.stripeAmount != null ? Number(raw.stripeAmount) : raw.stripe_amount != null ? Number(raw.stripe_amount) : null,
    };
  }

  private toStripeConfig(response: any): Observable<StripeFrontendConfig> {
    const publishableKey = response?.publishableKey || response?.publishable_key || response?.stripePublishableKey;
    if (!publishableKey) {
      return throwError(() => new Error('Stripe frontend config response is missing publishable key.'));
    }
    return of({ publishableKey });
  }
}
